/**
 * Ask-Agent chat — ported from `capacity/agent.tsx`, extended for the five-lever wire shape.
 * Renders as a vertical pane to the right of the levers (see `index.tsx`), which stay visible.
 * Every turn sends the live scenario plus the running conversation; the agent can reconfigure
 * the scenario mid-reply via `scenario_update`, dispatched straight into the reducer so the
 * dashboard moves live. A patch that changes several levers at once (e.g. a resolve) is applied
 * one lever at a time, the spotlight ring gliding to each, so it reads as a sequence of moves.
 */

import { ChevronRight, Loader2 } from "lucide-react";
import { type Dispatch, useEffect, useRef, useState } from "react";

import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { mergeScenario, spotlightForPatch, type SpotlightTarget } from "./spotlight";
import type { ScenariosAction } from "./state";
import {
  AgentStreamError,
  type CapacitySuggestion,
  type ScenarioHistoryMessage,
  type ScenarioPatch,
  type ScenarioStatePayload,
  streamScenarioAgent,
} from "./stream";

// The pre-first-turn defaults — kept verbatim from the original constant chips so the initial
// state is unchanged. Label and message are identical here (clicking sends exactly what's shown);
// only the agent-generated suggestions use the short-label / fuller-message split.
const DEFAULT_SUGGESTIONS: CapacitySuggestion[] = [
  { label: "Why are we short?", message: "Why are we short?" },
  { label: "Close the gap for me", message: "Close the gap for me" },
  { label: "What is the cheapest fix?", message: "What is the cheapest fix?" },
  {
    label: "What does pacing the intake actually mean?",
    message: "What does pacing the intake actually mean?",
  },
];

/** Levers grouped by the wire fields that move together, in the order the ring should visit
 *  them when one patch touches several at once. */
const LEVER_FIELD_GROUPS: (keyof ScenarioPatch)[][] = [
  ["year"],
  ["relo_threshold", "relo_excluded"],
  ["pack_count", "pack_excluded"],
  ["conv_count", "conv_excluded"],
  ["cap_system", "cap_units"],
  ["pace_pct"],
];

/** ms between each lever lighting up when a single patch changes several. */
const SEQUENCE_STEP_MS = 850;

/** Beat before the auto-asked first question submits, so the user's eyes settle on the freshly
 *  opened chat before the agent starts working. */
const INITIAL_ASK_DELAY_MS = 1500;

interface NarrationStep {
  id: string;
  text: string;
}

interface Message {
  id: number;
  role: "u" | "a";
  text: string;
  steps: NarrationStep[];
  active: boolean;
  failed?: boolean;
  previousScenario?: ScenarioStatePayload;
  appliedScenario?: boolean;
}

let nextMsgId = 0;

function openingFor(scenario: ScenarioStatePayload): Message {
  return {
    id: -1,
    role: "a",
    text: "Ask me anything about the levers, or tell me what to optimize for — I can apply changes live as I answer.",
    steps: [],
    active: false,
    previousScenario: scenario,
  };
}

interface ScenarioAgentProps {
  scenario: ScenarioStatePayload;
  dispatch: Dispatch<ScenariosAction>;
  onSpotlight: (target: SpotlightTarget | null) => void;
  /** A first question to fire automatically once, right after the chat opens (e.g. the welcome
   *  dialog's "See it in action"). Cleared via `onInitialAskConsumed` so it never repeats. */
  initialAsk?: string | null;
  onInitialAskConsumed?: () => void;
  onClose: () => void;
}

export function ScenarioAgent({
  scenario,
  dispatch,
  onSpotlight,
  initialAsk,
  onInitialAskConsumed,
  onClose,
}: ScenarioAgentProps) {
  const [messages, setMessages] = useState<Message[]>(() => [openingFor(scenario)]);
  const [draft, setDraft] = useState("");
  const [history, setHistory] = useState<ScenarioHistoryMessage[]>([]);
  const [suggestions, setSuggestions] = useState<CapacitySuggestion[]>(DEFAULT_SUGGESTIONS);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scenarioRef = useRef(scenario);
  scenarioRef.current = scenario;
  const spotlightTurn = useRef(0);
  // Pending timers for the one-lever-at-a-time reveal, so a new turn (or unmount) cancels them.
  const seqTimers = useRef<number[]>([]);
  const clearSequence = () => {
    seqTimers.current.forEach((id) => window.clearTimeout(id));
    seqTimers.current = [];
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => () => clearSequence(), []);

  // Fire the handed-in first question exactly once. Keyed on `initialAsk` so it fires whether the
  // chat mounts fresh with the seed already set or is already open when the seed arrives. The ref
  // guard survives StrictMode's dev-only double-mount, and consuming clears it upstream so
  // reopening the chat doesn't re-ask. A short beat before submitting lets the user's eyes settle
  // on the newly opened chat before the agent starts moving things.
  const askedInitial = useRef(false);
  useEffect(() => {
    // Seed cleared (consumed upstream, or none handed in) — drop the guard so a *later* seed
    // (e.g. reopening the welcome dialog and clicking again while the chat is still open) can
    // fire once more. Without this reset the one-way guard would silently swallow it.
    if (!initialAsk) {
      askedInitial.current = false;
      return undefined;
    }
    if (askedInitial.current) {
      return undefined;
    }
    const question = initialAsk;
    // Consume + guard only when the timer fires — not up front, or the resulting `initialAsk`
    // change would re-run this effect's cleanup and clear the timer before it ever fired.
    const timer = window.setTimeout(() => {
      askedInitial.current = true;
      onInitialAskConsumed?.();
      void ask(question);
    }, INITIAL_ASK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [initialAsk]);

  useEffect(() => {
    const body = bodyRef.current;
    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [messages]);

  async function ask(question: string) {
    const userId = nextMsgId++;
    const pendingId = nextMsgId++;
    const previousScenario = scenarioRef.current;
    setMessages((m) => [
      ...m,
      { id: userId, role: "u", text: question, steps: [], active: false },
      { id: pendingId, role: "a", text: "", steps: [], active: true, previousScenario },
    ]);

    const turn = ++spotlightTurn.current;
    clearSequence();
    let applied = scenarioRef.current;
    // How long to hold the ring after the reply lands — extended when a patch sequences.
    let spotlightHoldMs = 1600;
    onSpotlight({ key: "hero", label: "reading the scenario" });

    let reply = "";
    const steps: NarrationStep[] = [];
    let failed = false;
    let gotSuggestions = false;
    const patch = (fn: (msg: Message) => Message) =>
      setMessages((m) => m.map((msg) => (msg.id === pendingId ? fn(msg) : msg)));
    const upsertStep = (id: string, text: string, replace: boolean) => {
      const existing = steps.find((s) => s.id === id);
      if (!existing) {
        steps.push({ id, text });
      } else {
        existing.text = replace ? text : existing.text + text;
      }
      patch((msg) => ({ ...msg, steps: [...steps] }));
    };

    try {
      await streamScenarioAgent(question, scenarioRef.current, history, {
        onTextDelta: (text) => {
          reply += text;
          patch((msg) => ({ ...msg, text: reply }));
        },
        onNarrationDelta: (id, text) => upsertStep(id, text, false),
        onNarration: (n) => upsertStep(n.id, n.text, true),
        onScenarioUpdate: (scenarioPatch) => {
          logger.debug("[scenario-agent] applying scenario_update", scenarioPatch);
          patch((msg) => ({ ...msg, appliedScenario: true }));

          // Split into the levers this patch actually touches, in ring-visit order.
          const groups: ScenarioPatch[] = [];
          for (const fields of LEVER_FIELD_GROUPS) {
            const sub: ScenarioPatch = {};
            let touched = false;
            for (const field of fields) {
              if (scenarioPatch[field] !== undefined) {
                (sub as Record<string, unknown>)[field] = scenarioPatch[field];
                touched = true;
              }
            }
            if (touched) {
              groups.push(sub);
            }
          }
          if (groups.length === 0) {
            // No lever fields (e.g. an agent_set-only patch) — apply wholesale, no sequence.
            dispatch({ type: "applyPatch", patch: scenarioPatch });
            applied = mergeScenario(applied, scenarioPatch);
            return;
          }
          const applyStep = (sub: ScenarioPatch) => {
            if (spotlightTurn.current !== turn) {
              return;
            }
            dispatch({ type: "applyPatch", patch: sub });
            onSpotlight(spotlightForPatch(sub, applied));
            applied = mergeScenario(applied, sub);
          };
          // First lever immediately, carrying the agent-set tags so the "Agent set" badges light
          // up as the sequence starts (agent_set is absolute-replace, not per-lever). The rest
          // glide in on a stagger.
          const [firstGroup, ...restGroups] = groups;
          if (!firstGroup) {
            return;
          }
          applyStep(
            scenarioPatch.agent_set !== undefined
              ? { ...firstGroup, agent_set: scenarioPatch.agent_set }
              : firstGroup,
          );
          restGroups.forEach((sub, i) => {
            seqTimers.current.push(
              window.setTimeout(() => applyStep(sub), (i + 1) * SEQUENCE_STEP_MS),
            );
          });
          if (groups.length > 1) {
            spotlightHoldMs = Math.max(
              spotlightHoldMs,
              (groups.length - 1) * SEQUENCE_STEP_MS + 1500,
            );
          }
        },
        onSuggestions: (next) => {
          gotSuggestions = true;
          setSuggestions(next);
        },
        onError: (message) => {
          failed = true;
          logger.warn("[scenario-agent] stream error", message);
          patch((msg) => ({
            ...msg,
            active: false,
            failed: true,
            text: message || "Something went wrong answering that.",
          }));
        },
      });
      patch((msg) => ({ ...msg, active: false }));
      if (!failed && reply) {
        setHistory((h) => [
          ...h,
          { role: "user", content: question },
          { role: "assistant", content: reply },
        ]);
      }
      // A completed turn that produced no suggestions (backend skipped them, e.g. a silent
      // generation failure) falls back to the generic defaults rather than leaving the *previous*
      // turn's now-stale contextual chips in place. Done only at turn end, so the chips don't
      // flicker to defaults mid-stream on every turn. A failed turn keeps whatever was showing.
      if (!failed && !gotSuggestions) {
        setSuggestions(DEFAULT_SUGGESTIONS);
      }
    } catch (err) {
      logger.warn("[scenario-agent] request failed", err);
      const unavailable = err instanceof AgentStreamError && [501, 503].includes(err.status);
      patch((msg) => ({
        ...msg,
        active: false,
        failed: true,
        text: unavailable
          ? "The agent isn't available on this deployment yet — the scenario assistant hasn't been configured."
          : "Sorry — I couldn't reach the agent right now.",
      }));
    } finally {
      window.setTimeout(() => {
        if (spotlightTurn.current === turn) {
          onSpotlight(null);
        }
      }, spotlightHoldMs);
    }
  }

  function send() {
    const q = draft.trim();
    if (!q) {
      return;
    }
    setDraft("");
    void ask(q);
  }

  // The spark launches from and returns into the latest agent message bubble.
  const homeMsgId = [...messages].reverse().find((m) => m.role === "a")?.id;

  return (
    <div className="chat">
      <div className="chat-head">
        <span className="chat-dot" />
        <span className="chat-t">Inventory Agent</span>
        <span className="chat-s">UKDC Reading</span>
        <button className="chat-x" aria-label="Close chat" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="chat-msgs" ref={bodyRef}>
        {messages.map((m) => (
          <div
            className={m.role === "u" ? "msg user" : "msg agent"}
            key={m.id}
            data-spot={m.id === homeMsgId ? "agent-home" : undefined}
          >
            {m.role === "a" && m.steps.length ? <ReasoningDisclosure steps={m.steps} /> : null}
            {m.text}
            {m.active ? <Loader2 className="ml-1 inline h-3.5 w-3.5 animate-spin" /> : null}
            {m.role === "a" && m.appliedScenario && m.previousScenario ? (
              <RevertButton previousScenario={m.previousScenario} dispatch={dispatch} />
            ) : null}
          </div>
        ))}
      </div>
      <div className="chat-chips">
        {suggestions.map((s, i) => (
          <button
            className="chat-chip"
            // Index-suffixed so two model-generated suggestions with the same label can't collide.
            key={`${s.label}-${i}`}
            title={s.message}
            onClick={() => void ask(s.message)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="chat-in">
        <input
          ref={inputRef}
          placeholder="Ask about the levers, or tell me what to optimize…"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              send();
            }
          }}
        />
        <button className="chat-send" onClick={send}>
          Send
        </button>
      </div>
      <div className="chat-foot">
        <button className="rr-btn" onClick={onClose}>
          Back to levers
        </button>
      </div>
    </div>
  );
}

function ReasoningDisclosure({ steps }: { steps: NarrationStep[] }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="mb-1.5">
      <button
        type="button"
        className="text-muted-foreground flex items-center gap-1 text-[11px]"
        onClick={() => setExpanded((e) => !e)}
      >
        <ChevronRight className={cn("h-3 w-3 transition-transform", expanded && "rotate-90")} />
        Reasoning · {steps.length} step{steps.length === 1 ? "" : "s"}
      </button>
      {expanded ? (
        <ol className="border-border mt-1 flex flex-col gap-1 border-l pl-2">
          {steps.map((s) => (
            <li key={s.id} className="text-muted-foreground text-[11px] italic">
              {s.text}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

function RevertButton({
  previousScenario,
  dispatch,
}: {
  previousScenario: ScenarioStatePayload;
  dispatch: Dispatch<ScenariosAction>;
}) {
  const [reverted, setReverted] = useState(false);
  return (
    <button
      type="button"
      disabled={reverted}
      className="border-border text-muted-foreground hover:border-primary hover:text-primary mt-1.5 block rounded-md border px-2 py-1 text-[11px] transition-colors disabled:pointer-events-none disabled:opacity-60"
      onClick={() => {
        dispatch({ type: "restore", payload: previousScenario });
        setReverted(true);
      }}
    >
      {reverted ? "Reverted" : "Revert to previous scenario"}
    </button>
  );
}
